// frontend/src/lib/governanceAdjustedExposure.js

/**
 * AI Fine Protector™ — Governance Adjusted Exposure Overlay
 * Governance Engine 1.0 foundation
 *
 * This module implements Layer 2 (Regulatory Defensibility) as a conservative
 * overlay on the existing Layer 1 exposure output. Layer 1 remains the source
 * of truth and is never mutated here.
 *
 * Public positioning:
 * - Planning estimate only.
 * - Not legal advice, a legal conclusion, a compliance certification, or a
 *   regulatory guarantee.
 */

import { AUDIT_QUESTIONS } from "../data/questions";

import {
  classifyDataSensitivityProfile,
  classifyEvidenceReadiness,
  classifyGovernanceMaturity,
  classifyLayer2Confidence,
  classifyRemediationProgress,
  classifyUserImpactProfile,
  evaluateGovernanceValidationGate,
  getGravityFrameworkVersion,
  getInternalJurisdictionEnforcementMultiplier,
  getJurisdictionEnforcementProfile,
  getPublicModelNotice,
  rankExposureDrivers,
  rankRiskReductionDrivers,
} from "./gravityFramework.js";

const VERSION = "governance.adjusted.exposure.v1.engine1";
const RELEASE_CANDIDATE = "Sprint01-RC2";
const DEFAULT_DEBUG_INTERNAL_VALUES = false;

const MAX_GOVERNANCE_BENEFIT_NORMAL = 0.2;
const MAX_GOVERNANCE_BENEFIT_EXCEPTIONAL = 0.3;

const RESERVOIR_WEIGHTS = Object.freeze({
  remediation_progress: 0.4,
  resolved_risk_value: 0.25,
  evidence_readiness: 0.2,
  governance_maturity: 0.15,
});

const CRITICAL_QUESTION_IDS = new Set(
  AUDIT_QUESTIONS
    .filter((question) => Number(question?.gravity || 0) >= 10)
    .map((question) => String(question.id)),
);

function n(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function clamp(value, min, max) {
  const number = n(value, min);
  return Math.max(min, Math.min(max, number));
}

function roundMoney(value) {
  return Math.round(n(value, 0));
}

function roundPct(value, places = 1) {
  const factor = 10 ** places;
  return Math.round(n(value, 0) * factor) / factor;
}

function upper(value) {
  return String(value || "").trim().toUpperCase();
}

function lower(value) {
  return String(value || "").trim().toLowerCase();
}

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function nowIso() {
  try {
    return new Date().toISOString();
  } catch {
    return "—";
  }
}

function firstFinite(candidates = [], fallback = 0) {
  for (const candidate of candidates) {
    const number = Number(candidate);
    if (Number.isFinite(number)) return number;
  }
  return fallback;
}

function ratioLabel(value) {
  const pct = Math.round(clamp(value, 0, 1) * 100);
  if (pct >= 85) return "Very High";
  if (pct >= 65) return "High";
  if (pct >= 40) return "Moderate";
  if (pct >= 15) return "Low";
  return "Very Low";
}

function normalizeStateCode(value) {
  const raw = upper(value);
  const match = raw.match(/^US[_-]([A-Z]{2})$/);
  if (match?.[1]) return match[1];
  if (/^[A-Z]{2}$/.test(raw)) return raw;
  return "";
}

function extractQuestionId(value) {
  const match = String(value || "").match(/\d+/);
  return match?.[0] || "";
}

function sortLayer1Triplet({ low, expected, high }) {
  const values = [n(low, 0), n(expected, 0), n(high, 0)].filter((value) => Number.isFinite(value) && value >= 0);
  if (!values.length) return { low: 0, expected: 0, high: 0, normalized: false };

  const sorted = [...values].sort((a, b) => a - b);
  const normalized = !(n(low, 0) <= n(expected, 0) && n(expected, 0) <= n(high, 0));

  return {
    low: roundMoney(sorted[0] ?? 0),
    expected: roundMoney(sorted[Math.min(1, sorted.length - 1)] ?? sorted[0] ?? 0),
    high: roundMoney(sorted[sorted.length - 1] ?? 0),
    normalized,
  };
}

function makeEmptyResult(status) {
  return {
    version: VERSION,
    release_candidate: RELEASE_CANDIDATE,
    gravity_framework_version: getGravityFrameworkVersion(),
    generated_at_iso: nowIso(),
    enabled: false,
    status,
    public_model_notice: getPublicModelNotice(),
    layer_1: null,
    layer_2: null,
    layer_3: null,
  };
}

function getExposureInput({ exposure, canonicalPayload } = {}) {
  const source = isRecord(exposure) ? exposure : canonicalPayload?.risk_exposure;
  return isRecord(source) ? source : null;
}

function getAssessmentInput(canonicalPayload) {
  return isRecord(canonicalPayload?.assessment) ? canonicalPayload.assessment : {};
}

function getSummaryInput(canonicalPayload) {
  return isRecord(canonicalPayload?.summary) ? canonicalPayload.summary : {};
}

function getConfigInput(canonicalPayload) {
  return isRecord(canonicalPayload?.config) ? canonicalPayload.config : {};
}

function getPrimaryCurrency(exposure) {
  const headlineCode = upper(exposure?.headline?.currency_code);
  if (headlineCode) return headlineCode;
  const buckets = isRecord(exposure?.combined_by_currency) ? exposure.combined_by_currency : {};
  return upper(Object.keys(buckets || {})[0] || "USD") || "USD";
}

function getPrimarySymbol(exposure, currencyCode) {
  const headlineSymbol = String(exposure?.headline?.currency_symbol || "").trim();
  if (headlineSymbol) return headlineSymbol;
  const bucket = isRecord(exposure?.combined_by_currency?.[currencyCode]) ? exposure.combined_by_currency[currencyCode] : null;
  return bucket?.currency_symbol || (currencyCode === "GBP" ? "£" : currencyCode === "EUR" ? "€" : "$"
  );
}

function getLayer1Metrics(exposure) {
  const currencyCode = getPrimaryCurrency(exposure);
  const bucket = isRecord(exposure?.combined_by_currency?.[currencyCode]) ? exposure.combined_by_currency[currencyCode] : null;
  const headline = isRecord(exposure?.headline) ? exposure.headline : {};

  const rawHigh = firstFinite([bucket?.combined_structured_worst_case_native, headline?.combined_structured_worst_case_native], 0);
  const rawExpected = firstFinite([bucket?.combined_net_native, headline?.combined_net_native, headline?.combined_net_planning_case_native], 0);
  const rawLow = firstFinite([bucket?.combined_floor_native, headline?.combined_floor_native], 0);
  const normalized = sortLayer1Triplet({ low: rawLow, expected: rawExpected, high: rawHigh });

  return {
    currency_code: currencyCode,
    currency_symbol: getPrimarySymbol(exposure, currencyCode),
    high: normalized.high,
    expected: normalized.expected,
    low: normalized.low,
    normalization_applied: normalized.normalized,
  };
}

function getSelectedJurisdictions({ canonicalPayload, exposure } = {}) {
  const summary = getSummaryInput(canonicalPayload);
  const config = getConfigInput(canonicalPayload);
  const candidates = [
    ...safeArray(summary?.jurisdictions),
    ...safeArray(config?.__jurisdictions_normalized || config?.jurisdictions),
    ...safeArray(exposure?.headline?.regimes),
  ]
    .map((item) => upper(item))
    .filter(Boolean);

  return Array.from(new Set(candidates));
}

function getSelectedStates({ canonicalPayload, exposure } = {}) {
  const config = getConfigInput(canonicalPayload);
  const summary = getSummaryInput(canonicalPayload);
  const usStateDetail = isRecord(exposure?.per_regime?.US_STATE?.us_state_detail) ? exposure.per_regime.US_STATE.us_state_detail : {};
  const candidates = [
    ...safeArray(config?.selectedStates),
    ...safeArray(config?.selected_states),
    ...safeArray(config?.states_selected),
    ...safeArray(config?.us_states),
    ...safeArray(summary?.selectedStates),
    ...safeArray(summary?.selected_states),
    ...safeArray(usStateDetail?.selected_states),
  ];

  return Array.from(new Set(candidates.map(normalizeStateCode).filter(Boolean)));
}

function hasFallbackState(exposure) {
  const rows = safeArray(exposure?.per_regime?.US_STATE?.us_state_detail?.per_state);
  return rows.some((row) => Boolean(row?.fallback_state || row?.state_metadata?.fallback_state));
}

function hasFederalOverlay(exposure) {
  const fed = isRecord(exposure?.per_regime?.US_FED) ? exposure.per_regime.US_FED : null;
  return Boolean(fed?.inferred_overlay || exposure?.overlays?.inferred_federal_overlay?.inferred);
}

function resolveHighestEnforcementProfile({ states = [], jurisdictions = [] } = {}) {
  const orderedProfiles = ["BASELINE", "LOW", "MODERATE_LOW", "MODERATE", "MODERATE_HIGH", "HIGH", "VERY_HIGH"];
  const candidates = [];

  states.forEach((state) => candidates.push(getJurisdictionEnforcementProfile(state)));
  jurisdictions.forEach((jurisdiction) => {
    const token = upper(jurisdiction).replace(/-/g, "_");
    if (token === "EU" || token === "UK") candidates.push(getJurisdictionEnforcementProfile(token));
    if (token === "US_FED" || token === "US_FEDERAL") {
      candidates.push({
        jurisdiction: "US_FED",
        configured: true,
        profile: "MODERATE_HIGH",
        label: "Moderate High",
        confidence: "MEDIUM_HIGH",
        confidence_label: "Medium High",
        rationale: "Federal overlay exposure indicates potentially material federal regulatory surface.",
        source_basis: "AI Fine Protector federal overlay heuristic",
        customer_visible: true,
      });
    }
  });

  if (!candidates.length) return getJurisdictionEnforcementProfile("UNKNOWN");

  return candidates.reduce((best, current) => {
    const bestRank = orderedProfiles.indexOf(best?.profile || "BASELINE");
    const currentRank = orderedProfiles.indexOf(current?.profile || "BASELINE");
    return currentRank > bestRank ? current : best;
  }, candidates[0]);
}

function resolveHighestJurisdictionMultiplier({ states = [], jurisdictions = [] } = {}) {
  const candidates = [];
  states.forEach((state) => candidates.push(getInternalJurisdictionEnforcementMultiplier(state).multiplier));
  jurisdictions.forEach((jurisdiction) => {
    const token = upper(jurisdiction).replace(/-/g, "_");
    if (token === "EU" || token === "UK") candidates.push(getInternalJurisdictionEnforcementMultiplier(token).multiplier);
    if (token === "US_FED" || token === "US_FEDERAL") candidates.push(1.05);
  });

  if (!candidates.length) return 1;
  return Math.max(...candidates.filter((value) => Number.isFinite(value)));
}

function deriveEvidenceInputs({ evidenceSummary, canonicalPayload } = {}) {
  const assessment = getAssessmentInput(canonicalPayload);
  const totalControls = firstFinite([
    evidenceSummary?.totalControls,
    evidenceSummary?.total_controls,
    assessment?.total_questions,
    safeArray(assessment?.active_question_ids).length,
  ], 0);

  return {
    evidenceCount: firstFinite([evidenceSummary?.evidenceCount, evidenceSummary?.evidence_count, evidenceSummary?.uploaded_count], 0),
    supportedControls: firstFinite([evidenceSummary?.supportedControls, evidenceSummary?.supported_controls, evidenceSummary?.covered_controls], 0),
    totalControls,
  };
}

function deriveRemediationRows({ remediationSummary, canonicalPayload } = {}) {
  const sources = [
    remediationSummary?.rows,
    remediationSummary?.findings,
    remediationSummary?.items,
    canonicalPayload?.remediation_rows,
    canonicalPayload?.remediationRows,
    canonicalPayload?.remediation?.rows,
    canonicalPayload?.remediation?.findings,
  ];

  for (const source of sources) {
    if (Array.isArray(source) && source.length) return source.filter(isRecord);
  }

  const mapCandidates = [remediationSummary?.remediationMap, remediationSummary?.remediation_map, canonicalPayload?.remediation_map];
  for (const candidate of mapCandidates) {
    if (isRecord(candidate)) return Object.entries(candidate).map(([id, row]) => ({ id, ...(isRecord(row) ? row : {}) }));
  }

  return [];
}

function normalizeStatus(row) {
  return lower(row?.status || row?.remediation_status || row?.state || "open").replace(/\s+/g, "_");
}

function rowHasEvidence(row) {
  return Boolean(
    safeArray(row?.evidence_links).length ||
      safeArray(row?.uploads).length ||
      safeArray(row?.evidence).length ||
      safeArray(row?.files).length ||
      row?.hasEvidence ||
      row?.has_evidence
  );
}

function rowGravity(row) {
  const direct = firstFinite([row?.gravity, row?.severity, row?.risk_gravity], null);
  if (direct != null) return clamp(direct, 0, 10);
  const text = [row?.title, row?.text, row?.category, row?.question_text].map(lower).join(" | ");
  if (/(prohibited|social scoring|emotion recognition|biometric|children|medical|critical infrastructure)/.test(text)) return 10;
  if (/(employment|recruitment|credit|insurance|judicial|democratic|law enforcement)/.test(text)) return 8;
  if (/(sensitive|security|ropa|dpia|training data|adversarial|incident)/.test(text)) return 6;
  return 3;
}

function rowExposure(row, fallback = 0) {
  return firstFinite([
    row?.exposure_expected,
    row?.expected_exposure,
    row?.expected_native,
    row?.net_exposure_native,
    row?.exposure_high,
    row?.high_exposure,
    row?.structured_worst_case_native,
    row?.amount,
  ], fallback);
}

function isResolvedStatus(status) {
  const s = lower(status);
  return s === "closed" || s === "complete" || s === "completed" || s === "resolved";
}

function isMitigatedStatus(status) {
  const s = lower(status);
  return s === "mitigated" || s === "partially_mitigated";
}

function isInProgressStatus(status) {
  const s = lower(status);
  return s === "in_progress" || s === "in-progress" || s === "review" || s === "needs_review";
}

function deriveAssessmentFindingFallback(canonicalPayload) {
  const assessment = getAssessmentInput(canonicalPayload);
  const unresolvedIds = Array.from(
    new Set(
      [
        ...safeArray(assessment?.failed_question_ids),
        ...safeArray(assessment?.unsure_question_ids),
      ]
        .map((id) => String(id || "").trim())
        .filter(Boolean),
    ),
  );

  const questionSnapshot = safeArray(assessment?.question_snapshot);
  const gravityById = new Map(
    questionSnapshot
      .filter((question) => question?.id != null)
      .map((question) => [String(question.id), n(question?.gravity, 0)]),
  );

  const criticalOpenCount = questionSnapshot.length
    ? unresolvedIds.filter((id) => n(gravityById.get(id), 0) >= 8).length
    : 0;

  return {
    totalCount: unresolvedIds.length,
    criticalOpenCount,
  };
}

function deriveRemediationInputs({ remediationSummary, canonicalPayload } = {}) {
  const summary = isRecord(remediationSummary) ? remediationSummary : {};
  const rows = deriveRemediationRows({ remediationSummary, canonicalPayload });

  if (rows.length) {
    let closed = 0;
    let mitigated = 0;
    let inProgress = 0;
    let open = 0;
    let withEvidence = 0;
    let criticalOpen = 0;
    let criticalResolved = 0;

    rows.forEach((row) => {
      const status = normalizeStatus(row);
      const gravity = rowGravity(row);
      if (rowHasEvidence(row)) withEvidence += 1;
      if (isResolvedStatus(status)) {
        closed += 1;
        if (gravity >= 8) criticalResolved += 1;
      } else if (isMitigatedStatus(status)) {
        mitigated += 1;
        if (gravity >= 8) criticalResolved += 0.75;
      } else if (isInProgressStatus(status)) {
        inProgress += 1;
        if (gravity >= 8) criticalOpen += 1;
      } else {
        open += 1;
        if (gravity >= 8) criticalOpen += 1;
      }
    });

    return {
      completeCount: closed + mitigated,
      closedCount: closed,
      mitigatedCount: mitigated,
      inProgressCount: inProgress,
      openCount: open,
      totalCount: rows.length,
      evidenceLinkedFindings: withEvidence,
      criticalOpenCount: criticalOpen,
      criticalResolvedCount: criticalResolved,
      rows,
    };
  }

  const assessmentFallback = deriveAssessmentFindingFallback(canonicalPayload);
  const closed = firstFinite([summary?.closedCount, summary?.closed_count, summary?.closed], 0);
  const mitigated = firstFinite([summary?.mitigatedCount, summary?.mitigated_count, summary?.mitigated], 0);
  const complete = firstFinite([summary?.completeCount, summary?.complete_count, summary?.complete], closed + mitigated);
  const inProgress = firstFinite([summary?.inProgressCount, summary?.in_progress_count, summary?.in_progress], 0);
  const summaryTotal = firstFinite([summary?.totalCount, summary?.total_count, summary?.total], 0);
  const total = Math.max(summaryTotal, assessmentFallback.totalCount);
  const summaryCriticalOpen = firstFinite(
    [summary?.criticalOpenCount, summary?.critical_open_count, summary?.openCriticalFindings],
    0,
  );

  return {
    completeCount: complete,
    closedCount: closed,
    mitigatedCount: mitigated,
    inProgressCount: inProgress,
    openCount: Math.max(0, total - complete - inProgress),
    totalCount: total,
    evidenceLinkedFindings: firstFinite([summary?.evidenceLinkedFindings, summary?.evidence_linked_findings, summary?.findingsWithEvidence], 0),
    criticalOpenCount: Math.max(summaryCriticalOpen, assessmentFallback.criticalOpenCount),
    criticalResolvedCount: firstFinite([summary?.criticalResolvedCount, summary?.critical_resolved_count], 0),
    rows: [],
  };
}

function deriveAssessmentCompleteness(canonicalPayload) {
  const assessment = getAssessmentInput(canonicalPayload);
  const total = firstFinite([assessment?.total_questions, safeArray(assessment?.active_question_ids).length], 0);
  if (total <= 0) return 0;

  const answered = firstFinite([assessment?.answered_count, assessment?.answered_questions_count, total - n(assessment?.unanswered_count, 0)], 0);
  return clamp(answered / total, 0, 1);
}

function deriveMissingInputPenalty({ canonicalPayload, evidenceProfile, remediationProfile, dataSensitivityProfile } = {}) {
  const summary = getSummaryInput(canonicalPayload);
  const config = getConfigInput(canonicalPayload);
  let missing = 0;
  let total = 5;

  if (!firstFinite([summary?.annual_revenue_estimate, config?.annualTurnover], 0)) missing += 1;
  if (!firstFinite([summary?.people_impacted_per_month, config?.userImpact], 0)) missing += 1;
  if (!safeArray(summary?.jurisdictions || config?.jurisdictions).length) missing += 1;
  if (upper(evidenceProfile) === "LIMITED") missing += 1;
  if (upper(remediationProfile) === "MINIMAL") missing += 1;
  if (upper(dataSensitivityProfile) === "STANDARD" && !(summary?.industry || config?.industry || config?.niche)) {
    missing += 0.5;
    total += 0.5;
  }

  return clamp(missing / total, 0, 1);
}

function deriveHighRiskSectorFlag({ canonicalPayload, dataSensitivityProfile } = {}) {
  const summary = getSummaryInput(canonicalPayload);
  const config = getConfigInput(canonicalPayload);
  const text = [summary?.industry, summary?.sector, config?.industry, config?.sector, config?.niche]
    .map((value) => lower(value))
    .join(" | ");

  return ["health", "financial", "bank", "children", "education", "biometric", "employment", "insurance"].some((term) => text.includes(term)) ||
    ["HIGH", "CRITICAL"].includes(upper(dataSensitivityProfile));
}

function deriveSpecialRiskSignals({ canonicalPayload, dataSensitivityProfile, remediationInputs } = {}) {
  const summary = getSummaryInput(canonicalPayload);
  const config = getConfigInput(canonicalPayload);
  const assessment = getAssessmentInput(canonicalPayload);
  const text = [
    summary?.industry,
    summary?.sector,
    summary?.description,
    config?.industry,
    config?.sector,
    config?.niche,
    config?.description,
    config?.companyDescription,
    config?.useCase,
    config?.use_case,
    ...safeArray(assessment?.risk_signals),
    ...safeArray(assessment?.failed_question_ids),
    ...safeArray(assessment?.unsure_question_ids),
  ]
    .map(lower)
    .join(" | ");

  const failedIds = safeArray(assessment?.failed_question_ids).map(extractQuestionId).filter(Boolean);
  const unsureIds = safeArray(assessment?.unsure_question_ids).map(extractQuestionId).filter(Boolean);
  const unresolvedCriticalFromAssessment = [...failedIds, ...unsureIds].some((id) => CRITICAL_QUESTION_IDS.has(id));

  return {
    children_involved: /child|children|minor|student|school|education/.test(text),
    sensitive_data: ["ELEVATED", "HIGH", "CRITICAL"].includes(upper(dataSensitivityProfile?.profile || dataSensitivityProfile)) || /health|biometric|financial|credit|insurance|employment/.test(text),
    repeat_violations: /repeat|prior violation|previous violation|recidiv/.test(text),
    intentional_conduct: /intentional|willful|wilful|knowing|reckless/.test(text),
    prohibited_ai: /prohibited|social scoring|emotion recognition|biometric identification|predictive policing/.test(text),
    open_critical_findings: n(remediationInputs?.criticalOpenCount, 0) > 0 || unresolvedCriticalFromAssessment,
  };
}

function calculateGovernanceMaturityFill(governanceScore, governanceProfile) {
  const score = clamp(governanceScore, 0, 100);
  if (score > 0) return clamp(score / 100, 0, 1);
  const profile = upper(governanceProfile?.profile || governanceProfile);
  if (profile === "ADVANCED") return 0.9;
  if (profile === "ESTABLISHED") return 0.72;
  if (profile === "DEVELOPING") return 0.48;
  return 0.18;
}

function calculateRemediationProgressFill(remediationInputs) {
  const total = n(remediationInputs?.totalCount, 0);
  if (total <= 0) return 0;
  const closed = n(remediationInputs?.closedCount, 0);
  const mitigated = n(remediationInputs?.mitigatedCount, 0);
  const inProgress = n(remediationInputs?.inProgressCount, 0);
  return clamp((closed * 1 + mitigated * 0.75 + inProgress * 0.35) / total, 0, 1);
}

function calculateResolvedRiskValueFill({ remediationInputs, layer1Expected }) {
  const rows = safeArray(remediationInputs?.rows);
  const totalCount = n(remediationInputs?.totalCount, 0);
  if (rows.length) {
    const fallbackPerRow = rows.length ? n(layer1Expected, 0) / rows.length : 0;
    let totalWeight = 0;
    let resolvedWeight = 0;

    rows.forEach((row) => {
      const gravity = rowGravity(row);
      const exposure = Math.max(0, rowExposure(row, fallbackPerRow));
      const weight = Math.max(1, exposure) * Math.max(1, gravity) ** 1.15;
      const status = normalizeStatus(row);
      totalWeight += weight;
      if (isResolvedStatus(status)) resolvedWeight += weight;
      else if (isMitigatedStatus(status)) resolvedWeight += weight * 0.75;
      else if (isInProgressStatus(status)) resolvedWeight += weight * 0.25;
    });

    return totalWeight > 0 ? clamp(resolvedWeight / totalWeight, 0, 1) : 0;
  }

  if (totalCount <= 0) return 0;
  const criticalResolved = n(remediationInputs?.criticalResolvedCount, 0);
  const criticalOpen = n(remediationInputs?.criticalOpenCount, 0);
  const progress = calculateRemediationProgressFill(remediationInputs);
  const criticalBoost = criticalResolved > 0 ? Math.min(0.18, criticalResolved * 0.06) : 0;
  const criticalDrag = criticalOpen > 0 ? Math.min(0.28, criticalOpen * 0.07) : 0;
  return clamp(progress * 0.9 + criticalBoost - criticalDrag, 0, 1);
}

function calculateEvidenceReadinessFill({ evidenceInputs, evidenceProfile, remediationInputs }) {
  const profileCoverage = clamp(n(evidenceProfile?.coverage_ratio, 0), 0, 1);
  const totalControls = n(evidenceInputs?.totalControls, 0);
  const supportedRatio = totalControls > 0 ? clamp(n(evidenceInputs?.supportedControls, 0) / totalControls, 0, 1) : profileCoverage;

  const totalFindings = n(remediationInputs?.totalCount, 0);
  const findingEvidenceRatio = totalFindings > 0 ? clamp(n(remediationInputs?.evidenceLinkedFindings, 0) / totalFindings, 0, 1) : 0;
  const remediationFill = calculateRemediationProgressFill(remediationInputs);

  // Evidence is most valuable when it is connected to remediation.
  const remediationLinkedEvidence = totalFindings > 0 ? findingEvidenceRatio : Math.min(supportedRatio, remediationFill);
  return clamp(supportedRatio * 0.55 + remediationLinkedEvidence * 0.35 + Math.min(0.1, n(evidenceInputs?.evidenceCount, 0) / 100), 0, 1);
}

function calculateReservoirs({ layer1, governanceScore, governanceProfile, evidenceInputs, evidenceProfile, remediationInputs }) {
  const remediationFill = calculateRemediationProgressFill(remediationInputs);
  const resolvedRiskFill = calculateResolvedRiskValueFill({ remediationInputs, layer1Expected: layer1.expected });
  const evidenceFill = calculateEvidenceReadinessFill({ evidenceInputs, evidenceProfile, remediationInputs });
  const governanceFill = calculateGovernanceMaturityFill(governanceScore, governanceProfile);

  const weightedFill =
    remediationFill * RESERVOIR_WEIGHTS.remediation_progress +
    resolvedRiskFill * RESERVOIR_WEIGHTS.resolved_risk_value +
    evidenceFill * RESERVOIR_WEIGHTS.evidence_readiness +
    governanceFill * RESERVOIR_WEIGHTS.governance_maturity;

  return {
    remediation_progress: {
      key: "remediation_progress",
      label: "Remediation Progress",
      weight: RESERVOIR_WEIGHTS.remediation_progress,
      fill: roundPct(remediationFill, 3),
      contribution_ratio: roundPct(remediationFill * RESERVOIR_WEIGHTS.remediation_progress, 4),
    },
    resolved_risk_value: {
      key: "resolved_risk_value",
      label: "Resolved Risk Value",
      weight: RESERVOIR_WEIGHTS.resolved_risk_value,
      fill: roundPct(resolvedRiskFill, 3),
      contribution_ratio: roundPct(resolvedRiskFill * RESERVOIR_WEIGHTS.resolved_risk_value, 4),
    },
    evidence_readiness: {
      key: "evidence_readiness",
      label: "Evidence Readiness",
      weight: RESERVOIR_WEIGHTS.evidence_readiness,
      fill: roundPct(evidenceFill, 3),
      contribution_ratio: roundPct(evidenceFill * RESERVOIR_WEIGHTS.evidence_readiness, 4),
    },
    governance_maturity: {
      key: "governance_maturity",
      label: "Governance Maturity",
      weight: RESERVOIR_WEIGHTS.governance_maturity,
      fill: roundPct(governanceFill, 3),
      contribution_ratio: roundPct(governanceFill * RESERVOIR_WEIGHTS.governance_maturity, 4),
    },
    weighted_fill_ratio: roundPct(clamp(weightedFill, 0, 1), 4),
  };
}

function determineGovernanceBenefitCap({ reservoirs, governanceProfile, evidenceProfile, remediationInputs, jurisdictionProfile, dataSensitivityProfile, specialRiskSignals, highRiskSector }) {
  const blockers = [];
  const constraints = [];
  const remediationFill = n(reservoirs?.remediation_progress?.fill, 0);
  const evidenceFill = n(reservoirs?.evidence_readiness?.fill, 0);
  const governanceFill = n(reservoirs?.governance_maturity?.fill, 0);
  const resolvedRiskFill = n(reservoirs?.resolved_risk_value?.fill, 0);

  const exceptionalEligible =
    governanceFill >= 0.85 &&
    evidenceFill >= 0.75 &&
    remediationFill >= 0.8 &&
    resolvedRiskFill >= 0.75 &&
    !specialRiskSignals?.open_critical_findings &&
    !specialRiskSignals?.prohibited_ai &&
    !specialRiskSignals?.repeat_violations &&
    !specialRiskSignals?.intentional_conduct;

  let cap = exceptionalEligible ? MAX_GOVERNANCE_BENEFIT_EXCEPTIONAL : MAX_GOVERNANCE_BENEFIT_NORMAL;
  let basis = exceptionalEligible ? "EXCEPTIONAL" : "NORMAL";

  const jurisdiction = upper(jurisdictionProfile?.profile);
  if (jurisdiction === "VERY_HIGH") {
    cap -= 0.02;
    constraints.push("Very high jurisdiction gravity limits available governance benefit.");
  } else if (jurisdiction === "HIGH") {
    cap -= 0.015;
    constraints.push("High jurisdiction gravity limits available governance benefit.");
  }

  const evidenceProfileKey = upper(evidenceProfile?.profile);
  if (evidenceProfileKey === "LIMITED") {
    cap = Math.min(cap, 0.14);
    constraints.push("Limited evidence readiness materially constrains available governance benefit.");
  } else if (evidenceProfileKey === "DEVELOPING") {
    cap = Math.min(cap, 0.17);
    constraints.push("Developing evidence readiness modestly constrains available governance benefit.");
  }

  const sensitivity = upper(dataSensitivityProfile?.profile || dataSensitivityProfile);
  if (sensitivity === "CRITICAL") {
    cap -= 0.04;
    constraints.push("Critical data sensitivity limits available governance benefit.");
  } else if (sensitivity === "HIGH") {
    cap -= 0.025;
    constraints.push("High data sensitivity limits available governance benefit.");
  } else if (sensitivity === "ELEVATED") {
    cap -= 0.01;
    constraints.push("Elevated data sensitivity modestly limits available governance benefit.");
  }

  if (highRiskSector) {
    cap -= 0.01;
    constraints.push("High-risk sector profile limits available governance benefit.");
  }

  if (specialRiskSignals?.children_involved) {
    cap -= 0.03;
    constraints.push("Children or minors appear to be involved.");
  }
  if (specialRiskSignals?.sensitive_data) {
    cap -= 0.015;
    constraints.push("Sensitive data signals are present.");
  }
  if (specialRiskSignals?.repeat_violations) {
    cap -= 0.05;
    blockers.push("Repeat violation signals constrain available governance benefit.");
  }
  if (specialRiskSignals?.intentional_conduct) {
    cap -= 0.06;
    blockers.push("Intentional or knowing conduct signals constrain available governance benefit.");
  }
  if (specialRiskSignals?.prohibited_ai) {
    cap = Math.min(cap, 0.1);
    blockers.push("Potential prohibited AI practice constrains available governance benefit.");
  }
  if (specialRiskSignals?.open_critical_findings) {
    cap = Math.min(cap, 0.12);
    blockers.push("One or more unresolved critical findings constrain available governance benefit.");
  }

  cap = clamp(cap, 0, MAX_GOVERNANCE_BENEFIT_EXCEPTIONAL);

  return {
    basis,
    exceptional_eligible: exceptionalEligible,
    maximum_available_ratio: roundPct(cap, 4),
    maximum_available_pct: roundPct(cap * 100, 1),
    normal_maximum_pct: MAX_GOVERNANCE_BENEFIT_NORMAL * 100,
    exceptional_maximum_pct: MAX_GOVERNANCE_BENEFIT_EXCEPTIONAL * 100,
    constraints,
    blockers,
    profile_labels: {
      governance: governanceProfile?.label || "—",
      evidence: evidenceProfile?.label || "—",
      remediation: classifyRemediationProgress(remediationInputs)?.label || "—",
      jurisdiction: jurisdictionProfile?.label || "—",
      sensitivity: dataSensitivityProfile?.label || "—",
    },
  };
}

function calculateGovernanceCredit({ reservoirs, cap }) {
  const rawEarnedRatio = clamp(n(reservoirs?.weighted_fill_ratio, 0) * MAX_GOVERNANCE_BENEFIT_EXCEPTIONAL, 0, MAX_GOVERNANCE_BENEFIT_EXCEPTIONAL);
  const availableRatio = clamp(n(cap?.maximum_available_ratio, 0), 0, MAX_GOVERNANCE_BENEFIT_EXCEPTIONAL);
  const finalEarnedRatio = Math.min(rawEarnedRatio, availableRatio);
  const remainingOpportunityRatio = Math.max(0, availableRatio - finalEarnedRatio);
  const blockedOpportunityRatio = Math.max(0, rawEarnedRatio - finalEarnedRatio);

  return {
    raw_earned_ratio: roundPct(rawEarnedRatio, 4),
    raw_earned_pct: roundPct(rawEarnedRatio * 100, 1),
    final_earned_ratio: roundPct(finalEarnedRatio, 4),
    final_earned_pct: roundPct(finalEarnedRatio * 100, 1),
    maximum_available_ratio: roundPct(availableRatio, 4),
    maximum_available_pct: roundPct(availableRatio * 100, 1),
    remaining_opportunity_ratio: roundPct(remainingOpportunityRatio, 4),
    remaining_opportunity_pct: roundPct(remainingOpportunityRatio * 100, 1),
    blocked_opportunity_ratio: roundPct(blockedOpportunityRatio, 4),
    blocked_opportunity_pct: roundPct(blockedOpportunityRatio * 100, 1),
    label: `${roundPct(finalEarnedRatio * 100, 1)}% of ${roundPct(availableRatio * 100, 1)}% Available`,
  };
}

function calculateAdjustedRange({ layer1, governanceCredit }) {
  const credit = clamp(n(governanceCredit?.final_earned_ratio, 0), 0, MAX_GOVERNANCE_BENEFIT_EXCEPTIONAL);
  const ratio = 1 - credit;
  const expected = Math.max(0, n(layer1.expected, 0));
  const low = Math.max(0, n(layer1.low, 0));
  const high = Math.max(0, n(layer1.high, 0));

  if (expected <= 0 || high <= 0) {
    return {
      low: 0,
      midpoint: 0,
      high: 0,
      reduction_from_expected_native: 0,
      reduction_from_expected_pct: 0,
      movement_direction: "NO_LAYER_1_EXPECTED_VALUE",
    };
  }

  const adjustedLow = Math.min(low, roundMoney(low * ratio));
  const midpoint = Math.min(expected, roundMoney(expected * ratio));
  const adjustedHigh = Math.min(high, roundMoney(high * ratio));
  const ordered = sortLayer1Triplet({ low: adjustedLow, expected: midpoint, high: adjustedHigh });
  const delta = roundMoney(expected - ordered.expected);

  return {
    low: roundMoney(ordered.low),
    midpoint: roundMoney(ordered.expected),
    high: roundMoney(ordered.high),
    reduction_from_expected_native: delta,
    reduction_from_expected_pct: expected > 0 ? roundPct((delta / expected) * 100, 1) : 0,
    movement_direction: delta > 0 ? "REDUCTION" : "UNCHANGED",
  };
}

function calculateExposurePosition({ layer1, midpoint }) {
  const low = n(layer1.low, 0);
  const high = n(layer1.high, 0);
  if (high <= low) return { ratio: 0.5, label: "Mid Range" };
  const ratio = clamp((midpoint - low) / (high - low), 0, 1);
  if (ratio <= 0.25) return { ratio, label: "Lower Range" };
  if (ratio <= 0.6) return { ratio, label: "Middle Range" };
  return { ratio, label: "Upper Range" };
}

function calculateLayer3({ layer1, remediationInputs, reservoirs, governanceCredit }) {
  const rows = safeArray(remediationInputs?.rows);
  let totalExposure = 0;
  let addressedExposure = 0;
  let unresolvedExposure = 0;
  let resolvedFindings = 0;
  let criticalRemaining = n(remediationInputs?.criticalOpenCount, 0);
  let evidenceSupported = n(remediationInputs?.evidenceLinkedFindings, 0);

  if (rows.length) {
    const fallbackPerRow = rows.length ? n(layer1.expected, 0) / rows.length : 0;
    totalExposure = 0;
    addressedExposure = 0;
    unresolvedExposure = 0;
    resolvedFindings = 0;
    criticalRemaining = 0;
    evidenceSupported = 0;

    rows.forEach((row) => {
      const amount = Math.max(0, rowExposure(row, fallbackPerRow));
      const status = normalizeStatus(row);
      const gravity = rowGravity(row);
      totalExposure += amount;
      if (rowHasEvidence(row)) evidenceSupported += 1;
      if (isResolvedStatus(status)) {
        addressedExposure += amount;
        resolvedFindings += 1;
      } else if (isMitigatedStatus(status)) {
        addressedExposure += amount * 0.75;
        resolvedFindings += 1;
      } else {
        unresolvedExposure += amount;
        if (gravity >= 8) criticalRemaining += 1;
      }
    });
  } else {
    const totalCount = n(remediationInputs?.totalCount, 0);
    const resolvedValueFill = n(reservoirs?.resolved_risk_value?.fill, 0);
    totalExposure = n(layer1.expected, 0);
    addressedExposure = totalExposure * resolvedValueFill;
    unresolvedExposure = Math.max(0, totalExposure - addressedExposure);
    resolvedFindings = n(remediationInputs?.closedCount, 0) + n(remediationInputs?.mitigatedCount, 0);
    evidenceSupported = n(remediationInputs?.evidenceLinkedFindings, 0);
    if (totalCount <= 0) unresolvedExposure = totalExposure;
  }

  const totalFindings = n(remediationInputs?.totalCount, rows.length);
  const evidenceCoverage = totalFindings > 0 ? clamp(evidenceSupported / totalFindings, 0, 1) : 0;

  return {
    title: "Remediation Impact",
    headline_metric: "Exposure Reduction Through Remediation",
    currency_code: layer1.currency_code,
    currency_symbol: layer1.currency_symbol,
    exposure_reduction_through_remediation_native: roundMoney(addressedExposure),
    remaining_unresolved_exposure_native: roundMoney(unresolvedExposure || Math.max(0, totalExposure - addressedExposure)),
    total_modeled_finding_exposure_native: roundMoney(totalExposure || layer1.expected),
    findings_resolved: roundMoney(resolvedFindings),
    findings_total: roundMoney(totalFindings),
    critical_findings_remaining: roundMoney(criticalRemaining),
    evidence_coverage_ratio: roundPct(evidenceCoverage, 4),
    evidence_coverage_pct: roundPct(evidenceCoverage * 100, 1),
    governance_credit_earned_pct: governanceCredit.final_earned_pct,
    maximum_governance_credit_available_pct: governanceCredit.maximum_available_pct,
    remaining_governance_opportunity_pct: governanceCredit.remaining_opportunity_pct,
    blocked_governance_opportunity_pct: governanceCredit.blocked_opportunity_pct,
    public_notice:
      "Layer 3 measures modeled exposure associated with findings addressed through remediation. It does not predict future fines prevented or eliminate historical exposure.",
  };
}

function buildNextBestActions({ remediationInputs, cap, reservoirs, governanceCredit }) {
  const actions = [];
  const rows = safeArray(remediationInputs?.rows);

  if (cap?.blockers?.length) {
    actions.push({
      id: "clear-blocker",
      label: "Clear governance-credit blocker",
      rationale: cap.blockers[0],
      category: "constraint",
      estimated_governance_benefit_pct: Math.min(4, Math.max(1, governanceCredit.blocked_opportunity_pct || 1)),
    });
  }

  if (rows.length) {
    const candidates = rows
      .filter((row) => !isResolvedStatus(normalizeStatus(row)) && !isMitigatedStatus(normalizeStatus(row)))
      .map((row) => ({
        row,
        priority: rowExposure(row, 0) * Math.max(1, rowGravity(row)) + (rowHasEvidence(row) ? 0 : 1000),
      }))
      .sort((a, b) => b.priority - a.priority)
      .slice(0, 2);

    candidates.forEach(({ row }, index) => {
      actions.push({
        id: `remediate-${row?.id || row?.question_id || index + 1}`,
        label: `Prioritize ${row?.question_number ? `Q${row.question_number}` : row?.title || "high-value finding"}`,
        rationale: "This unresolved finding appears to carry relatively high modeled exposure or gravity.",
        category: "remediation",
        estimated_governance_benefit_pct: Math.max(1, Math.min(5, Math.round(rowGravity(row) / 2))),
      });
    });
  }

  if (n(reservoirs?.evidence_readiness?.fill, 0) < 0.55) {
    actions.push({
      id: "improve-evidence",
      label: "Attach evidence to remediated high-value findings",
      rationale: "Evidence readiness is limiting regulatory confidence and explainability.",
      category: "evidence",
      estimated_governance_benefit_pct: 2,
    });
  }

  if (n(reservoirs?.governance_maturity?.fill, 0) < 0.6) {
    actions.push({
      id: "improve-governance",
      label: "Strengthen ownership, policy, and monitoring controls",
      rationale: "Governance maturity remains a secondary but durable driver of defensibility.",
      category: "governance",
      estimated_governance_benefit_pct: 1.5,
    });
  }

  return actions.slice(0, 3);
}

function buildPolicyExplanation({ governanceCredit, cap }) {
  const parts = [];
  parts.push(`Governance Credit Earned is ${governanceCredit.label}.`);
  if (governanceCredit.remaining_opportunity_pct > 0) {
    parts.push(`${governanceCredit.remaining_opportunity_pct}% governance benefit remains available within the current cap.`);
  }
  if (cap?.blockers?.length) {
    parts.push(`Available benefit is constrained by: ${cap.blockers.join(" ")}`);
  }
  return parts.join(" ");
}



function buildInvariantChecks(result) {
  const checks = [];
  const layer1 = result?.layer_1 || {};
  const layer2 = result?.layer_2 || {};
  const credit = layer2?.governance_credit || {};
  const maxBenefit = layer2?.maximum_governance_benefit || {};

  const layer1High = n(layer1.high_native, 0);
  const layer1Expected = n(layer1.expected_native, 0);
  const layer1Low = n(layer1.low_native, 0);
  const layer2High = n(layer2.high_native, 0);
  const layer2Expected = n(layer2.midpoint_native, 0);
  const layer2Low = n(layer2.low_native, 0);
  const earned = n(credit.final_earned_pct, 0);
  const available = n(credit.maximum_available_pct, n(maxBenefit.maximum_available_pct, 0));
  const remaining = n(credit.remaining_opportunity_pct, 0);
  const blocked = n(credit.blocked_opportunity_pct, 0);

  const push = (id, severity, passed, message) => {
    checks.push({ id, severity, passed: Boolean(passed), message });
  };

  push("INV-01", "CRITICAL", layer2High <= layer1High, "Layer 2 high must not exceed Layer 1 high.");
  push("INV-02", "CRITICAL", layer2Expected <= layer1Expected, "Layer 2 expected/midpoint must not exceed Layer 1 expected.");
  push("INV-03", "CRITICAL", layer2Low <= layer1Low, "Layer 2 low must not exceed Layer 1 low.");
  push("INV-04", "CRITICAL", earned >= 0, "Governance Credit Earned must never be negative.");
  push("INV-05", "CRITICAL", earned <= available, "Governance Credit Earned must not exceed maximum available governance benefit.");
  push("INV-06", "MAJOR", remaining >= 0, "Remaining governance opportunity must not be negative.");
  push("INV-07", "MAJOR", blocked >= 0, "Blocked governance opportunity must not be negative.");
  push("INV-08", "MAJOR", layer1Low <= layer1Expected && layer1Expected <= layer1High, "Layer 1 range must remain ordered.");
  push("INV-09", "MAJOR", layer2Low <= layer2Expected && layer2Expected <= layer2High, "Layer 2 range must remain ordered.");
  push("INV-10", "MAJOR", Boolean(result?.layer_3), "Layer 3 must be present as a reporting layer and must not mutate Layer 1.");

  const failed = checks.filter((check) => !check.passed);
  return {
    valid: failed.length === 0,
    release_blocked: failed.some((check) => check.severity === "CRITICAL" || check.severity === "MAJOR"),
    total: checks.length,
    passed: checks.length - failed.length,
    failed: failed.length,
    checks,
  };
}

export function validateGovernanceAdjustedExposureResult(result) {
  if (!isRecord(result) || result.enabled !== true || result.status !== "OK") {
    return {
      valid: false,
      release_blocked: true,
      total: 1,
      passed: 0,
      failed: 1,
      checks: [
        {
          id: "INV-00",
          severity: "CRITICAL",
          passed: false,
          message: "Governance Engine result must be an enabled OK result before invariant validation can pass.",
        },
      ],
    };
  }
  return buildInvariantChecks(result);
}

/**
 * Public API (MUST NOT THROW)
 */
export function computeGovernanceAdjustedExposure({ exposure, canonicalPayload, evidenceSummary, remediationSummary, debugInternalValues = DEFAULT_DEBUG_INTERNAL_VALUES } = {}) {
  try {
    const layer1Exposure = getExposureInput({ exposure, canonicalPayload });
    if (!layer1Exposure) return makeEmptyResult("NO_LAYER_1_EXPOSURE");

    const assessment = getAssessmentInput(canonicalPayload);
    const summary = getSummaryInput(canonicalPayload);
    const config = getConfigInput(canonicalPayload);

    const layer1 = getLayer1Metrics(layer1Exposure);
    if (layer1.expected <= 0 || layer1.high <= 0) return makeEmptyResult("NO_LAYER_1_EXPECTED_VALUE");

    const selectedStates = getSelectedStates({ canonicalPayload, exposure: layer1Exposure });
    const selectedJurisdictions = getSelectedJurisdictions({ canonicalPayload, exposure: layer1Exposure });
    const jurisdictionProfile = resolveHighestEnforcementProfile({ states: selectedStates, jurisdictions: selectedJurisdictions });
    const enforcementMultiplier = resolveHighestJurisdictionMultiplier({ states: selectedStates, jurisdictions: selectedJurisdictions });

    const peopleImpacted = firstFinite([summary?.people_impacted_per_month, config?.userImpact], 0);
    const governanceScore = firstFinite([
      assessment?.governance_maturity_score_pct,
      summary?.governance_maturity_score_pct,
      assessment?.control_coverage_pct,
      assessment?.compliance_score_pct,
      summary?.control_coverage_pct,
      summary?.compliance_score_pct,
    ], 0);

    const governanceProfile = classifyGovernanceMaturity(governanceScore);
    const userImpactProfile = classifyUserImpactProfile(peopleImpacted);
    const dataSensitivityProfile = classifyDataSensitivityProfile({
      industry: summary?.industry || config?.industry,
      niche: config?.niche,
      sector: summary?.sector || config?.sector,
      description: config?.description || config?.companyDescription || config?.useCase || config?.use_case,
      assessmentSignals: [
        ...safeArray(assessment?.risk_signals),
        ...safeArray(assessment?.failed_question_ids),
        ...safeArray(assessment?.unsure_question_ids),
      ],
    });

    const evidenceInputs = deriveEvidenceInputs({ evidenceSummary, canonicalPayload });
    const evidenceProfile = classifyEvidenceReadiness(evidenceInputs);
    const remediationInputs = deriveRemediationInputs({ remediationSummary, canonicalPayload });
    const remediationProfile = classifyRemediationProgress(remediationInputs);

    const validationGate = evaluateGovernanceValidationGate({
      governanceProfile: governanceProfile.profile,
      evidenceProfile: evidenceProfile.profile,
    });

    const reservoirs = calculateReservoirs({
      layer1,
      governanceScore,
      governanceProfile,
      evidenceInputs,
      evidenceProfile,
      remediationInputs,
    });

    const highRiskSector = deriveHighRiskSectorFlag({ canonicalPayload, dataSensitivityProfile: dataSensitivityProfile.profile });
    const specialRiskSignals = deriveSpecialRiskSignals({ canonicalPayload, dataSensitivityProfile, remediationInputs });
    const cap = determineGovernanceBenefitCap({
      reservoirs,
      governanceProfile,
      evidenceProfile,
      remediationInputs,
      jurisdictionProfile,
      dataSensitivityProfile,
      specialRiskSignals,
      highRiskSector,
    });

    const governanceCredit = calculateGovernanceCredit({ reservoirs, cap });
    const adjustedRange = calculateAdjustedRange({ layer1, governanceCredit });
    const exposurePosition = calculateExposurePosition({ layer1, midpoint: adjustedRange.midpoint });
    const layer3 = calculateLayer3({ layer1, remediationInputs, reservoirs, governanceCredit });
    const nextBestActions = buildNextBestActions({ remediationInputs, cap, reservoirs, governanceCredit });

    const evidenceCoverage = n(evidenceProfile.coverage_ratio, 0);
    const remediationCompleteness = n(remediationProfile.completion_ratio, 0);
    const missingInputPenalty = deriveMissingInputPenalty({
      canonicalPayload,
      evidenceProfile: evidenceProfile.profile,
      remediationProfile: remediationProfile.profile,
      dataSensitivityProfile: dataSensitivityProfile.profile,
    });

    const confidence = classifyLayer2Confidence({
      assessmentCompleteness: deriveAssessmentCompleteness(canonicalPayload),
      evidenceCoverage,
      remediationCompleteness,
      missingInputPenalty,
    });

    const exposureDrivers = rankExposureDrivers({
      governanceProfile: governanceProfile.profile,
      enforcementProfile: jurisdictionProfile.profile,
      dataSensitivityProfile: dataSensitivityProfile.profile,
      userImpactProfile: userImpactProfile.profile,
      evidenceProfile: evidenceProfile.profile,
      remediationProfile: remediationProfile.profile,
      jurisdictionCount: selectedJurisdictions.length + selectedStates.length,
      hasFallbackState: hasFallbackState(layer1Exposure),
      hasFederalOverlay: hasFederalOverlay(layer1Exposure),
      highRiskSector,
    });

    const riskReductionDrivers = rankRiskReductionDrivers({
      governanceProfile: governanceProfile.profile,
      evidenceProfile: evidenceProfile.profile,
      remediationProfile: remediationProfile.profile,
      dataSensitivityProfile: dataSensitivityProfile.profile,
      userImpactProfile: userImpactProfile.profile,
    });

    return {
      version: VERSION,
      release_candidate: RELEASE_CANDIDATE,
      gravity_framework_version: getGravityFrameworkVersion(),
      generated_at_iso: nowIso(),
      enabled: true,
      status: "OK",
      public_model_notice: getPublicModelNotice(),
      methodology: {
        name: "AI Fine Protector Governance Engine 1.0",
        layer_1: "Regulatory Reality",
        layer_2: "Regulatory Defensibility",
        layer_3: "Remediation Impact",
        customer_visible_governance_metric: "Governance Credit Earned",
        internal_concept: "Regulatory confidence attribution",
      },
      layer_1: {
        title: "Baseline Regulatory Exposure Range",
        subtitle: "Statutory and regulatory planning range before governance adjustments.",
        currency_code: layer1.currency_code,
        currency_symbol: layer1.currency_symbol,
        high_native: layer1.high,
        expected_native: layer1.expected,
        low_native: layer1.low,
        normalization_applied: Boolean(layer1.normalization_applied),
      },
      layer_2: {
        title: "Governance-Adjusted Exposure Range",
        subtitle:
          "Exposure modelling based on governance maturity, evidence readiness, remediation progress, user impact profile, data sensitivity, and regulatory context.",
        currency_code: layer1.currency_code,
        currency_symbol: layer1.currency_symbol,
        low_native: adjustedRange.low,
        midpoint_native: adjustedRange.midpoint,
        high_native: adjustedRange.high,
        reduction_from_expected_native: adjustedRange.reduction_from_expected_native,
        reduction_from_expected_pct: adjustedRange.reduction_from_expected_pct,
        movement_direction: adjustedRange.movement_direction,
        exposure_position: exposurePosition,
        confidence,
        validation_gate: validationGate,
        governance_credit: governanceCredit,
        maximum_governance_benefit: cap,
        confidence_reservoirs: reservoirs,
        special_risk_signals: specialRiskSignals,
        policy_explanation: buildPolicyExplanation({ governanceCredit, cap }),
        profiles: {
          jurisdiction_enforcement: jurisdictionProfile,
          governance_maturity: governanceProfile,
          evidence_readiness: evidenceProfile,
          remediation_progress: remediationProfile,
          user_impact: userImpactProfile,
          data_sensitivity: dataSensitivityProfile,
        },
        drivers: {
          top_exposure_drivers: exposureDrivers,
          top_risk_reduction_drivers: riskReductionDrivers,
          next_best_actions: nextBestActions,
        },
        guardrails: {
          applied: true,
          public_summary: "Governance Engine 1.0 applies conservative caps, constraints, and monotonic guardrails.",
          raw_value_visibility: "INTERNAL_ONLY_DO_NOT_DISPLAY",
          ...(debugInternalValues
            ? {
                __internal_debug: {
                  enforcementMultiplier,
                  reservoirs,
                  cap,
                  governanceCredit,
                },
              }
            : {}),
        },
      },
      layer_3: layer3,
    };
  } catch (error) {
    return {
      version: VERSION,
      release_candidate: RELEASE_CANDIDATE,
      gravity_framework_version: getGravityFrameworkVersion(),
      generated_at_iso: nowIso(),
      enabled: false,
      status: "ERROR",
      public_model_notice: getPublicModelNotice(),
      layer_1: null,
      layer_2: null,
      layer_3: null,
      __error: String(error?.message || error || "Governance adjusted exposure error"),
    };
  }
}

export function getGovernanceAdjustedExposureReference() {
  return Object.freeze({
    version: VERSION,
    release_candidate: RELEASE_CANDIDATE,
    methodology_name: "AI Fine Protector Governance Engine 1.0",
    public_model_notice: getPublicModelNotice(),
    layer_1_title: "Baseline Regulatory Exposure Range",
    layer_1_subtitle: "Statutory and regulatory planning range before governance adjustments.",
    layer_2_title: "Governance-Adjusted Exposure Range",
    layer_2_subtitle:
      "Exposure modelling based on governance maturity, evidence readiness, remediation progress, user impact profile, data sensitivity, and regulatory context.",
    layer_3_title: "Remediation Impact",
    governance_credit_label: "Governance Credit Earned",
    customer_visible_profiles: Object.freeze([
      "Jurisdiction Enforcement Profile",
      "Governance Maturity",
      "Evidence Readiness",
      "Remediation Progress",
      "User Impact Profile",
      "Data Sensitivity Profile",
      "Layer 2 Confidence",
      "Governance Credit Earned",
      "Remaining Governance Opportunity",
    ]),
    hidden_values_policy: "Raw numeric multipliers are internal modelling values and should not be displayed without approved explanatory context.",
  });
}

export function validateGovernanceAdjustedExposureSmokeTest() {
  const exposure = {
    headline: {
      currency_code: "USD",
      currency_symbol: "$",
      combined_structured_worst_case_native: 500000,
      combined_net_native: 350000,
      combined_floor_native: 200000,
      regimes: ["US_STATE"],
    },
    combined_by_currency: {
      USD: {
        currency_code: "USD",
        currency_symbol: "$",
        combined_structured_worst_case_native: 500000,
        combined_net_native: 350000,
        combined_floor_native: 200000,
      },
    },
    per_regime: {
      US_STATE: {
        us_state_detail: {
          selected_states: ["CA"],
          per_state: [{ state: "CA", fallback_state: false }],
        },
      },
    },
  };

  const result = computeGovernanceAdjustedExposure({
    exposure,
    canonicalPayload: {
      summary: {
        jurisdictions: ["US_STATE"],
        selectedStates: ["CA"],
        people_impacted_per_month: 25000,
        annual_revenue_estimate: 10000000,
        industry: "Healthcare",
      },
      assessment: {
        compliance_score_pct: 82,
        total_questions: 60,
        answered_count: 58,
      },
      config: { selectedStates: ["CA"] },
    },
    evidenceSummary: { evidenceCount: 12, supportedControls: 30, totalControls: 60 },
    remediationSummary: { completeCount: 20, closedCount: 14, mitigatedCount: 6, inProgressCount: 10, totalCount: 40, criticalOpenCount: 0 },
  });

  const invariantValidation = validateGovernanceAdjustedExposureResult(result);

  return {
    valid:
      invariantValidation.valid === true &&
      result?.enabled === true &&
      result?.status === "OK" &&
      result?.layer_1?.expected_native === 350000 &&
      Number.isFinite(Number(result?.layer_2?.midpoint_native)) &&
      result?.layer_2?.midpoint_native > 0 &&
      result?.layer_2?.midpoint_native <= result?.layer_1?.expected_native &&
      result?.layer_2?.governance_credit?.final_earned_pct >= 0 &&
      result?.layer_3?.headline_metric === "Exposure Reduction Through Remediation",
    invariantValidation,
    result,
  };
}
